import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { Panel } from "../../components/ui/Panel";
import type { Pipeline } from "../../types";
import { PIPELINE_COLORS, SavedFlash, describeMinutes, inputClass, slugify } from "./shared";

export function PipelineSettings({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Pipeline> }) =>
      api.updatePipeline(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipelines });
      // Targets feed every countdown on screen; refetch the lists too.
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
      setSavedAt(Date.now());
    },
  });

  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PIPELINE_COLORS[0]!);
  const [newSla, setNewSla] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const minutes = newSla.trim() === "" ? null : Number(newSla.trim());
      return api.createPipeline({
        name: newName.trim(),
        slug: slugify(newName),
        color: newColor,
        slaMinutes: minutes,
        position: (pipelines.data ?? []).length,
        isActive: true,
      } as Omit<Pipeline, "id">);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipelines });
      setNewName("");
      setNewSla("");
      setAddError(null);
    },
    onError: (cause: unknown) =>
      setAddError(cause instanceof Error ? cause.message : "Could not add"),
  });

  /*
   * A pipeline whose slug collides with an existing one would be rejected by
   * the server, so it is caught here where the message can point at the field.
   */
  const slugTaken = (pipelines.data ?? []).some(
    (entry) => entry.slug === slugify(newName),
  );
  const canAdd = newName.trim().length > 0 && !slugTaken && !create.isPending;

  return (
    <Panel
      collapsible
      title="Pipelines and SLA targets"
    >
      <div className="space-y-1">
        {(pipelines.data ?? []).map((pipeline) => (
          <PipelineRow
            key={pipeline.id}
            pipeline={pipeline}
            isAdmin={isAdmin}
            pending={update.isPending}
            savedAt={savedAt}
            onPatch={(patch) => update.mutate({ id: pipeline.id, patch })}
          />
        ))}
      </div>

      {isAdmin ? (
        <div className="mt-4 border-t border-line pt-3">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
            Add a pipeline
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Pipeline name"
              className={inputClass + " h-8 w-48"}
            />
            <input
              type="number"
              min={1}
              value={newSla}
              onChange={(event) => setNewSla(event.target.value)}
              placeholder="No SLA"
              title="Target in business minutes. Leave blank for an untimed pipeline."
              className={inputClass + " h-8 w-28"}
            />

            <ColorPicker value={newColor} onChange={setNewColor} />

            <Button onClick={() => create.mutate()} disabled={!canAdd}>
              {create.isPending ? "Adding…" : "Add"}
            </Button>
          </div>

          {slugTaken && newName.trim() ? (
            <p className="mt-1.5 text-xs text-amber-600">
              There is already a pipeline with that name.
            </p>
          ) : null}
          {addError ? (
            <p className="mt-1.5 text-xs text-red-600">{addError}</p>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * One pipeline: its name, its colour, its target, and the way to retire it.
 *
 * Everything commits on blur or on click rather than behind a Save button,
 * matching the rest of this page.
 */
function PipelineRow({
  pipeline,
  isAdmin,
  pending,
  savedAt,
  onPatch,
}: {
  pipeline: Pipeline;
  isAdmin: boolean;
  pending: boolean;
  savedAt: number | null;
  onPatch: (patch: Partial<Pipeline>) => void;
}) {
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.deletePipeline(pipeline.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipelines });
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
      setConfirming(false);
    },
  });

  /*
   * The server's own words. It knows how many tickets are in the way and this
   * component does not, and a generic "could not delete" would send an admin
   * looking for a reason the response already gave them.
   */
  const blocked =
    remove.error instanceof ApiError && remove.error.status === 409
      ? remove.error.message
      : null;
  const failed = remove.error !== null && blocked === null;

  return (
    <div
      className={cn(
        "rounded-md px-2 py-1.5 hover:bg-surface-2",
        !pipeline.isActive && "opacity-60",
      )}
    >
      <div className="flex items-center gap-2 md:gap-3">
        {/*
          The colour swatch doubles as the button that changes it. It is the
          dot that appears beside this pipeline's name on every ticket in the
          app, so the thing you click to change it should be that dot.
        */}
        <button
          type="button"
          disabled={!isAdmin}
          onClick={() => setPicking((value) => !value)}
          aria-label={`Colour for ${pipeline.name}`}
          aria-expanded={picking}
          title={isAdmin ? "Change this pipeline's colour" : undefined}
          style={{ backgroundColor: pipeline.color }}
          className={cn(
            "size-4 shrink-0 rounded-full ring-offset-2 ring-offset-surface",
            isAdmin ? "hover:ring-2 hover:ring-line" : "cursor-default",
            picking && "ring-2 ring-content",
          )}
        />

        {/*
          The name is editable, but the slug it was created from is not: saved
          views and filters key on the slug, and rewriting it on a rename would
          quietly empty every view that referred to this pipeline.
        */}
        <input
          key={`${pipeline.id}:${pipeline.name}`}
          defaultValue={pipeline.name}
          disabled={!isAdmin}
          aria-label={`Name of ${pipeline.name}`}
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next === "" || next === pipeline.name) {
              event.target.value = pipeline.name;
              return;
            }
            onPatch({ name: next });
          }}
          className={cn(
            /* Takes the free width on a phone, where a fixed 11rem beside the
               SLA box cut "Technical" to "Technica". */
            "min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm text-content md:w-44 md:flex-none",
            "hover:border-line focus:border-line focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            "disabled:hover:border-transparent",
          )}
        />

        <input
          type="number"
          min={1}
          defaultValue={pipeline.slaMinutes ?? ""}
          placeholder="No SLA"
          disabled={!isAdmin}
          aria-label={`SLA target for ${pipeline.name}`}
          onBlur={(event) => {
            const raw = event.target.value.trim();
            const next = raw === "" ? null : Number(raw);
            if (next === pipeline.slaMinutes) return;
            if (next !== null && (!Number.isFinite(next) || next < 1)) return;
            onPatch({ slaMinutes: next });
          }}
          className={cn(
            "h-8 w-20 shrink-0 rounded-md border border-line bg-surface px-2 text-sm text-content md:w-28",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            "disabled:opacity-60",
          )}
        />
        <span className="shrink-0 whitespace-nowrap text-xs text-muted">
          {pipeline.slaMinutes === null
            ? "untimed"
            : describeMinutes(pipeline.slaMinutes)}
        </span>

        {!pipeline.isActive ? (
          <span
            title="Hidden from every picker. Existing tickets keep it."
            className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-faint"
          >
            inactive
          </span>
        ) : null}

        <span className="ml-auto flex items-center gap-2">
          <SavedFlash pending={pending} at={savedAt} />

          {isAdmin ? (
            !pipeline.isActive ? (
              <button
                type="button"
                onClick={() => onPatch({ isActive: true })}
                className="text-[11px] text-brand-600 hover:text-brand-700"
              >
                Reactivate
              </button>
            ) : null
          ) : null}

          {/*
            Two clicks, not a browser confirm: this is one row in a list and a
            modal for it is heavier than the action. The second click is
            labelled with what it does rather than "OK".
          */}
          {isAdmin ? (
            confirming ? (
              <span className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                  className="text-[11px] text-sla-breached hover:underline disabled:opacity-50"
                >
                  {remove.isPending ? "Removing…" : "Remove"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(false);
                    remove.reset();
                  }}
                  className="text-[11px] text-faint hover:text-brand-600 dark:hover:text-brand-400"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                aria-label={`Remove ${pipeline.name}`}
                title="Remove this pipeline"
                className="text-xs text-faint hover:text-sla-breached"
              >
                ✕
              </button>
            )
          ) : null}
        </span>
      </div>

      {picking && isAdmin ? (
        <div className="mt-2 pl-7">
          <ColorPicker
            value={pipeline.color}
            onChange={(color) => {
              onPatch({ color });
              setPicking(false);
            }}
          />
        </div>
      ) : null}

      {blocked ? (
        <div className="mt-1.5 pl-7 text-[11px] text-amber-600">
          {blocked}{" "}
          <button
            type="button"
            onClick={() => {
              onPatch({ isActive: false });
              setConfirming(false);
              remove.reset();
            }}
            className="underline hover:text-brand-600 dark:hover:text-brand-400"
          >
            Deactivate it
          </button>
        </div>
      ) : null}

      {failed ? (
        <p className="mt-1.5 pl-7 text-[11px] text-red-600">
          Could not remove that pipeline.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The eight-odd fixed hues.
 *
 * A fixed set rather than a free colour input: a pipeline colour is a 6px dot,
 * and eight distinguishable dots beats an arbitrary one nobody can tell from
 * its neighbour.
 */
function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {PIPELINE_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          aria-label={`Colour ${color}`}
          aria-pressed={value === color}
          style={{ backgroundColor: color }}
          className={cn(
            "size-5 rounded-full ring-offset-2 ring-offset-surface",
            value === color && "ring-2 ring-content",
          )}
        />
      ))}
    </div>
  );
}
